import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { pages } from "./page-builder";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Site type enums
// ---------------------------------------------------------------------------

export const SITE_TYPES = [
  "website",
  "funnel",
  "blog",
  "link_in_bio",
] as const;

export type SiteType = (typeof SITE_TYPES)[number];

export const SITE_STATUSES = ["draft", "published", "archived"] as const;
export type SiteStatus = (typeof SITE_STATUSES)[number];

export const NAV_TYPES = ["main", "footer", "mobile"] as const;
export type NavType = (typeof NAV_TYPES)[number];

export const FUNNEL_STEP_TYPES = [
  "capture",
  "upsell",
  "downsell",
  "order_bump",
  "thank_you",
] as const;

export type FunnelStepType = (typeof FUNNEL_STEP_TYPES)[number];

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface SiteTheme {
  presetId?: string;
  colors?: {
    brand?: string;
    brandDark?: string;
    secondary?: string;
    accent?: string;
    text?: string;
    background?: string;
    surface?: string;
    muted?: string;
  };
  fonts?: {
    heading?: { family: string; weight?: string; googleFont?: string };
    body?: { family: string; weight?: string; googleFont?: string };
  };
  customCss?: string;
  borderRadius?: string;
  spacing?: string;
  buttonStyle?: "solid" | "soft" | "outline" | "ghost";
  cardStyle?: "flat" | "outline" | "shadow" | "elevated";
}

export interface NavItem {
  id: string;
  label: string;
  url?: string;
  pageId?: string;
  openInNewTab?: boolean;
  children?: NavItem[];
}

export interface FunnelRoutingRule {
  condition: {
    field: string;
    operator: "equals" | "contains" | "not_equals" | "greater_than";
    value: string;
  };
  targetStepId: string;
}

// ---------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------

export const sites = pgTable(
  "sites",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    slug: text("slug").notNull(),
    name: text("name").notNull(),
    siteType: text("site_type").notNull().default("website"),
    status: text("status").notNull().default("draft"),
    customDomainId: uuid("custom_domain_id"),
    homePageId: uuid("home_page_id"),
    globalTheme: jsonb("global_theme").$type<SiteTheme>().default({}),
    headScripts: text("head_scripts"),
    bodyEndScripts: text("body_end_scripts"),
    seoDefaults: jsonb("seo_defaults")
      .$type<Record<string, unknown>>()
      .default({}),
    headerContent: jsonb("header_content").$type<Record<string, unknown>>(),
    footerContent: jsonb("footer_content").$type<Record<string, unknown>>(),
    robotsTxt: text("robots_txt"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_sites_tenant_slug").on(table.tenantId, table.slug),
    index("idx_sites_tenant_status").on(table.tenantId, table.status),
    index("idx_sites_tenant").on(table.tenantId),
    check(
      "chk_sites_type",
      sql`${table.siteType} IN ('website', 'funnel', 'blog', 'link_in_bio')`,
    ),
    check(
      "chk_sites_status",
      sql`${table.status} IN ('draft', 'published', 'archived')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Site navigation
// ---------------------------------------------------------------------------

export const siteNavigation = pgTable(
  "site_navigation",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    navType: text("nav_type").notNull().default("main"),
    items: jsonb("items").$type<NavItem[]>().default([]),
    updatedBy: text("updated_by").notNull(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_site_nav_site_type").on(table.siteId, table.navType),
    index("idx_site_nav_tenant").on(table.tenantId),
    check(
      "chk_nav_type",
      sql`${table.navType} IN ('main', 'footer', 'mobile')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Funnel steps
// ---------------------------------------------------------------------------

export const funnelSteps = pgTable(
  "funnel_steps",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    pageId: uuid("page_id")
      .notNull()
      .references(() => pages.id, { onDelete: "cascade" }),
    stepNumber: integer("step_number").notNull(),
    stepType: text("step_type").notNull().default("capture"),
    nextStepId: uuid("next_step_id"),
    routingRules: jsonb("routing_rules")
      .$type<FunnelRoutingRule[]>()
      .default([]),
  },
  (table) => [
    uniqueIndex("idx_funnel_steps_site_number").on(
      table.siteId,
      table.stepNumber,
    ),
    index("idx_funnel_steps_site").on(table.siteId),
    index("idx_funnel_steps_tenant").on(table.tenantId),
    check(
      "chk_funnel_step_type",
      sql`${table.stepType} IN ('capture', 'upsell', 'downsell', 'order_bump', 'thank_you')`,
    ),
  ],
);
